import { memo, useCallback } from "react";
import { useSelector } from "react-redux";

import LaunchIcon from "@material-ui/icons/Launch";
import { EvaChart } from "constants/charts.enums";

import { useChartsContext } from "../../../contexts";
import { useUser } from "../../../hooks";
import { useChartEntities } from "../../../hooks/charts";
import { extractActiveAndProperties } from "../../../hooks/charts/useChartSessions/utils";
import { EntityKind } from "../../../models/entityKind";
import { RootState } from "../../../store/rootReducer";
import {
  selectCheckedGlobalForecasts,
  selectCheckedGlobalTypeWells,
  useForecastStore
} from "../../../stores/forecast";
import { useMapStore } from "../../../stores/map";
import type { PopoutChannelState } from "../../../types/popout";
import ToolbarButton from "../../base/ToolbarButton";

const getPopoutUrl = (channel: string, entityKind: EntityKind) =>
  `/popout/chart/${channel}?channel=${channel}&entityKind=${entityKind}`;
const Popout = () => {
  const { sessionId } = useUser();
  const { id, entityKind } = useChartsContext();
  const { chartType, product, features, locks } = useChartEntities(id);
  // Popout renders the shared well/midstream ChartWrapper, which doesn't know
  // about the Rig/Frac Charts widget's entity shape — disable rather than open
  // a broken popout for it.
  const isDisabled =
    chartType === EvaChart.RigFracActivity ||
    chartType === EvaChart.RigFracRecentActivity;

  const channel = useSelector((state: RootState) => state.channel.channel);
  const filterId = useSelector((state: RootState) =>
    entityKind == EntityKind.Well ? state.filter.filterId : state.facilityFilter.filterId
  );
  const groupBy = useSelector((state: RootState) =>
    entityKind == EntityKind.Well
      ? state.groupBy.globalGroupBy
      : state.groupBy.globalFacilityFocus
  );
  const activeColorPalette = useSelector((state: RootState) =>
    entityKind == EntityKind.Well
      ? state.userSetting.activeColorPalette
      : state.userSetting.activeFacilityColorPalette
  );
  const colorPalettes = useSelector(
    (state: RootState) => state.userSetting.colorPalettes
  );
  const checkedGlobalTypeWellsFromStore = useForecastStore(selectCheckedGlobalTypeWells);
  const checkedGlobalTypeWells =
    entityKind == EntityKind.Well ? checkedGlobalTypeWellsFromStore : [];
  const checkedGlobalForecastsFromStore = useForecastStore(selectCheckedGlobalForecasts);
  const checkedGlobalForecasts =
    entityKind == EntityKind.Well ? checkedGlobalForecastsFromStore : [];
  const globalNormalizeBy = useSelector(
    (state: RootState) => state.normalizeBy.globalNormalizeBy
  );
  const useNormalizeBy = useSelector(
    (state: RootState) => state.normalizeBy.useNormalizeBy
  );
  const syncWells = useSelector((state: RootState) => state.app.syncWells);
  const forecastMode = useSelector((state: RootState) => state.app.forecastMode);
  const useForecastStartDate = useSelector(
    (state: RootState) => state.app.useForecastStartDate
  );
  const userForecastSettings = useSelector(
    (state: RootState) => state.app.userForecastSettings
  );

  const popout = useCallback(() => {
    sessionStorage.removeItem(channel);
    // Read at click time: subscribing would re-render this toolbar button on
    // every map transaction for a value only the click needs.
    const txnId = useMapStore.getState().txnId;
    const globalGroupBy = { ...groupBy };
    const extracted = extractActiveAndProperties(features);

    const payload: PopoutChannelState = {
      chartType,
      product,
      features: extracted,
      locks,
      filterId,
      sessionId,
      globalGroupBy,
      checkedGlobalTypeWells,
      checkedGlobalForecasts,
      globalNormalizeBy,
      useNormalizeBy,
      activeColorPalette,
      colorPalettes,
      syncWells,
      forecastMode,
      useForecastStartDate,
      userForecastSettings,
      txnId
    };
    sessionStorage.setItem(channel, JSON.stringify(payload));

    window.open(getPopoutUrl(channel, entityKind));
  }, [
    channel,
    chartType,
    product,
    features,
    locks,
    entityKind,
    id,
    filterId,
    sessionId,
    checkedGlobalTypeWells,
    checkedGlobalForecasts,
    globalNormalizeBy,
    useNormalizeBy,
    activeColorPalette,
    colorPalettes,
    syncWells,
    forecastMode,
    useForecastStartDate,
    userForecastSettings
  ]);

  return (
    <ToolbarButton
      active={false}
      icon={<LaunchIcon fontSize="large" />}
      overflowLabel="Pop out"
      tooltipText="Open in new Tab"
      data-testid="popout-toggle"
      onToggle={() => popout()}
      disabled={isDisabled}
    />
  );
};

export default memo(Popout);
